import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { getHeaders } from "../ApiHeaders/apiHeadersEntitlements";
import { FaSearch } from "react-icons/fa";
import {
  EXTERNAL_TENANT_USERS_QUERY,
  isExternalTenantUser,
} from "../Admin/constants/entitlementDomains";

interface User {
  loginName: string;
  domain: string;
  userId: string;
}

interface SearchableListProps {
  selectedItems: User[];
  onSelectionChange: (selectedItems: User[]) => void;
  disabled?: boolean;
  preserveItemOrder?: boolean;
  reorderSelectedOnOutside?: boolean;
}

const apiUrl = `${process.env.REACT_APP_ENTITLEMENTS_API || ""}/user/?${EXTERNAL_TENANT_USERS_QUERY}`;

const SearchableList: React.FC<SearchableListProps> = ({
  selectedItems,
  disabled = false,
  onSelectionChange,
  preserveItemOrder = true,
  reorderSelectedOnOutside = false,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirstMode, setSelectedFirstMode] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setSearchTerm("");
        if (reorderSelectedOnOutside) {
          setSelectedSnapshot(selectedItems.map((item) => item.userId));
          setSelectedFirstMode(true);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [reorderSelectedOnOutside, selectedItems]);

  useEffect(() => {
    if (disabled && reorderSelectedOnOutside) {
      setSelectedSnapshot(selectedItems.map((item) => item.userId));
      setSelectedFirstMode(true);
    }
  }, [disabled, reorderSelectedOnOutside, selectedItems]);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      const headers = await getHeaders();
      try {
        const response = await axios.get(apiUrl, {
          headers,
        });
        // console.log("API Response:", response.data);

        if (response.data?.response?.data) {
          const mappedUsers = response.data.response.data
            .filter(isExternalTenantUser)
            .filter(
              (user: any) => user.login_name || user.email || user.user_id
            )
            .map((user: any) => ({
              // firstName: user.first_name || "null",
              loginName: user.login_name || "null",
              domain: user.email || "",
              userId: String(user.user_id || ""),
            }));
          setUsers(mappedUsers);
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    fetchUsers();
  }, []);

  // const filteredItems = useMemo(
  //   () =>
  //     users.filter(
  //       (user) =>
  //         user.loginName.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //         user.domain.toLowerCase().includes(searchTerm.toLowerCase())
  //     ),
  //   [users, searchTerm]
  // );

  // const filteredItems = useMemo(() => {
  //   const query = searchTerm.toLowerCase();
  //   if (!query) return users;

  //   return users.filter((user) => {
  //     const loginName = user.loginName?.toLowerCase() || "";
  //     const domain = user.domain?.toLowerCase() || "";
  //     return loginName.includes(query) || domain.includes(query);
  //   });
  //   setSearchTerm("");
  // }, [users, searchTerm]);
  const filteredItems = useMemo(() => {
    const query = searchTerm.toLowerCase();

    const matchesQuery = (user: User) => {
      const loginName = user.loginName?.toLowerCase() || "";
      const domain = user.domain?.toLowerCase() || "";
      return loginName.includes(query) || domain.includes(query);
    };

    if (selectedFirstMode && reorderSelectedOnOutside) {
      const snapshotSet = new Set(selectedSnapshot);

      if (preserveItemOrder) {
        const selected = users.filter((user) => snapshotSet.has(user.userId) && matchesQuery(user));
        const unselected = users.filter((user) => !snapshotSet.has(user.userId) && matchesQuery(user));
        return [...selected, ...unselected];
      }

      const selected = users
        .filter((user) => snapshotSet.has(user.userId) && matchesQuery(user))
        .sort((a, b) => a.loginName.localeCompare(b.loginName));

      const unselected = users
        .filter((user) => !snapshotSet.has(user.userId) && matchesQuery(user))
        .sort((a, b) => a.loginName.localeCompare(b.loginName));

      return [...selected, ...unselected];
    }

    if (preserveItemOrder) {
      return users.filter((user) => matchesQuery(user));
    }

    const selectedSet = new Set(selectedItems.map((u) => u.userId));

    const selected = users
      .filter((user) => selectedSet.has(user.userId) && matchesQuery(user))
      .sort((a, b) => a.loginName.localeCompare(b.loginName));

    const unselected = users
      .filter((user) => !selectedSet.has(user.userId) && matchesQuery(user))
      .sort((a, b) => a.loginName.localeCompare(b.loginName));

    return [...selected, ...unselected];
  }, [
    users,
    selectedItems,
    searchTerm,
    preserveItemOrder,
    reorderSelectedOnOutside,
    selectedFirstMode,
    selectedSnapshot,
  ]);

  const isSelected = (item: User) =>
    selectedItems.some((selected) => selected.userId === item.userId);

  const toggleItemSelection = (item: User) => {
    const updatedSelection = isSelected(item)
      ? selectedItems.filter((selected) => selected.userId !== item.userId)
      : [...selectedItems, item];
    onSelectionChange(updatedSelection);
  };

  return (
    <div ref={wrapperRef} className="space-y-4 w-full max-w-4xl mx-auto p-4">
      {/* Search Input */}
      <div className="relative w-full">
        <FaSearch className="absolute top-3.5 left-3 text-gray-500 w-5 h-5" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          // onBlur={() => setSearchTerm("")}
          className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:border-blue-500 bg-gray-50 text-gray-800 shadow-inner text-sm"
          placeholder="Search here"
          aria-label="Search items"
        />
      </div>

      {/* Filtered Items List */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow min-h-56 max-h-[18rem] overflow-y-auto">
        {filteredItems.length > 0 ? (
          <>
            {/* Table Header */}
            <div className="grid  grid-cols-3 items-center text-gray-600 text-sm font-semibold mb-2 gap-x-3">
              <span>Login Name</span>
              <span>Domain</span>
              <span className="text-right">Select</span>
            </div>

            {/* User List */}
            {filteredItems.map((user) => (
              <div
                key={user.userId}
                className="grid grid-cols-3 items-center py-2 hover:bg-blue-50 rounded-lg cursor-pointer"
                onClick={() => !disabled && toggleItemSelection(user)}
              >
                <span className="text-gray-800 text-sm">{user.loginName}</span>
                <span className="text-gray-800 text-sm">
                  {user.domain || "No Domain"}
                </span>
                <div className="flex ml-30 2xl:mr-2 justify-end">
                  <label
                    className="flex items-center space-x-2 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(user)}
                      onChange={() => toggleItemSelection(user)}
                      className="form-checkbox w-4 h-4 text-blue-500"
                      aria-label={`Select ${user.loginName}`}
                      disabled={disabled}
                    />
                  </label>
                </div>
              </div>
            ))}
          </>
        ) : (
          <p className="text-gray-500 text-sm text-center">No items found.</p>
        )}
      </div>
    </div>
  );
};

export default SearchableList;
